/* rv-review-rail.js: <rv-review-rail> (sections/rv-review-rail.liquid; PLAN.md §4 B4). Deferred; loads only with it.
   1. Previous/Next page the row by the cards in view (scrollBy; 'auto' under reduced motion) and are disabled at
      the ends (focus moves to the other arrow if the focused one gets disabled). No autoplay, ever.
   2. "Read more" shows only on a card whose text the CSS clamped (the clamp applies only once this element is
      defined, so without JS every review shows in full). It opens that one card (aria-expanded, label swap).
   3. Top-up (data-topup="1"): once the row is within 800px of the viewport, read Judge.me's public reviews feed
      (reviews_grid_widget_data, per_page 10, as many pages as Judge.me's own total_pages says, with a 50-page
      safety bound, stops on an empty page). When every page loaded and the distinct ids reach Judge.me's
      total_count, the list is complete: a server card none of whose ids is in it (a review hidden or deleted in
      Judge.me after the metafield snapshot was written) is removed. Otherwise nothing is removed. Group the copies by their
      normalized title|text|name (Judge.me's import holds 20 reviews twice under different ids), and add every group
      with no copy already on the page (data-rv-ids), in the row's own order (photo cards first when the section
      says so, then newest first; if a card lands before the one in view, the row keeps that card where it was).
      The card markup mirrors the Liquid one. Review text goes in with textContent only;
      photos only from https://review-images.judgeme.com/; product links only to /products/<handle>. Any failure
      leaves the server cards as they are, silently. */
(() => {
  if (customElements.get('rv-review-rail')) return;

  const D = document;
  const FEED = 'https://api.judge.me/widgets/reviews_grid_widget_data';
  const PER_PAGE = 10;
  const MAX_PAGES = 50; /* a safety bound only: the loop follows Judge.me's total_pages and stops on an empty page */
  const PIC = /^https:\/\/review-images\.judgeme\.com\/[^\s?#"'<>]+$/;
  const PRODUCT = /^\/products\/[^\s/?#"'<>]+$/;
  const IMPORTED = 'review_collected_from_another_provider';
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const STAR = 'M12 2.6l2.4 6.5 6.9.3-5.4 4.3 1.9 6.6L12 16.5l-5.8 3.8 1.9-6.6-5.4-4.3 6.9-.3Z';
  /* The photo slot is the card's content box: min(340px, 100vw - 2 x 16px - 48px) minus 2 x 20px padding, so 300px
     from 420px up (media conditions, not min(), so every browser parses it). Same string in the Liquid. */
  const SIZES = '(min-width: 420px) 300px, calc(100vw - 120px)';

  const str = (v) => (v == null ? '' : String(v));
  /* Judge.me HTML-escapes titles: decode the five entities once (as the Liquid does), then textContent */
  const dec = (s) =>
    str(s)
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  const norm = (s) =>
    dec(s)
      .toLowerCase()
      .replace(/[‘’]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  const fill = (t, map) => str(t).replace(/\[(\w+)\]/g, (m, k) => (Object.prototype.hasOwnProperty.call(map, k) ? str(map[k]) : m));
  const make = (tag, cls, text) => {
    const e = D.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  };
  const picBase = (p) => {
    const u = str(p && (p.huge || p.original || p.small)).split('?')[0];
    return PIC.test(u) ? u : '';
  };
  const picUrl = (b, w) => b + '?quality=80&width=' + w;
  /* The UTC calendar date at noon, as the Liquid ('%b %-d, %Y' of YYYY-MM-DDT12:00:00Z) */
  const fmtDate = (d) => {
    const t = new Date(d + 'T12:00:00Z');
    return isNaN(t.getTime()) ? '' : t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* Judge.me's own badge for a review it imported from another provider */
  const isImported = (r) => Array.isArray(r && r.transparency_badges) && r.transparency_badges.indexOf(IMPORTED) !== -1;

  class RvReviewRail extends HTMLElement {
    connectedCallback() {
      if (this.sc) {
        /* moved in the DOM: the element's own listeners survive; only the window one was removed */
        if (this.onResize) window.addEventListener('resize', this.onResize);
        return;
      }
      this.sc = this.querySelector('.rv-revrail__scroller');
      this.list = this.querySelector('[data-rv-list]');
      if (!this.sc || !this.list) return;
      this.prev = this.querySelector('[data-rv-dir="-1"]');
      this.next = this.querySelector('[data-rv-dir="1"]');
      this.n = 0;

      this.addEventListener('click', (e) => {
        const dir = e.target.closest('[data-rv-dir]');
        if (dir && !dir.disabled) return this.page(+dir.dataset.rvDir || 1);
        const more = e.target.closest('.rv-revcard__more');
        if (more) this.toggle(more);
      });

      let raf = 0;
      const onScroll = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          this.ends();
        });
      };
      this.sc.addEventListener('scroll', onScroll, { passive: true });

      let width = this.sc.clientWidth;
      let timer = 0;
      this.onResize = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          if (this.sc.clientWidth === width) return;
          width = this.sc.clientWidth;
          this.clamp();
          this.ends();
        }, 150);
      };
      window.addEventListener('resize', this.onResize);
      if (D.fonts && D.fonts.ready) D.fonts.ready.then(() => this.clamp()).catch(() => {});
      this.clamp();
      this.ends();

      if (this.dataset.topup === '1' && this.dataset.shop) {
        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver(
            (entries) => {
              if (!entries.some((x) => x.isIntersecting)) return;
              io.disconnect();
              this.topUp();
            },
            { rootMargin: '1600px 0px' } /* early: the page prints only the first few cards (speed 2026-10-11) */
          );
          io.observe(this);
        } else {
          this.topUp();
        }
      }
    }

    /* The theme editor re-renders the section: drop the window listener of the old element */
    disconnectedCallback() {
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    /* ---------- 1. Arrows ---------- */
    page(dir) {
      const s = this.sc;
      const card = this.list.querySelector('.rv-revcard');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
      const step = card.getBoundingClientRect().width + gap;
      if (!step) return;
      const ps = getComputedStyle(s);
      const view = s.clientWidth - (parseFloat(ps.paddingLeft) || 0) - (parseFloat(ps.paddingRight) || 0);
      const n = Math.max(1, Math.floor((view + gap) / step));
      s.scrollBy({ left: dir * n * step, behavior: reduce() ? 'auto' : 'smooth' });
    }

    ends() {
      if (!this.prev || !this.next) return;
      const s = this.sc;
      const max = s.scrollWidth - s.clientWidth;
      const atStart = s.scrollLeft <= 1;
      const atEnd = s.scrollLeft >= max - 1;
      const had = D.activeElement;
      this.prev.disabled = atStart;
      this.next.disabled = atEnd;
      if (had === this.prev && atStart && !atEnd) this.next.focus({ preventScroll: true });
      if (had === this.next && atEnd && !atStart) this.prev.focus({ preventScroll: true });
    }

    /* ---------- 2. Read more ---------- */
    /* Read every body first, then write every button: one layout pass, not one per card. The button's display
       doesn't change the body's clamped box, so the result is the same as card by card. */
    clamp() {
      const rows = Array.from(this.list.querySelectorAll('.rv-revcard:not(.is-open)'))
        .map((c) => [c.querySelector('.rv-revcard__body'), c.querySelector('.rv-revcard__more')])
        .filter(([b, m]) => b && m);
      const need = rows.map(([b]) => b.scrollHeight > b.clientHeight + 1);
      rows.forEach(([, m], i) => {
        m.hidden = !need[i];
      });
    }

    toggle(m) {
      const c = m.closest('.rv-revcard');
      if (!c) return;
      const open = !c.classList.contains('is-open');
      c.classList.toggle('is-open', open);
      m.setAttribute('aria-expanded', String(open));
      m.textContent = (open ? this.dataset.less : this.dataset.more) || m.textContent;
    }

    /* ---------- 3. Top-up from Judge.me's public feed ---------- */
    async topUp() {
      const d = this.dataset;
      let pages = Math.min(MAX_PAGES, Math.ceil((+d.total || 0) / PER_PAGE) || MAX_PAGES);
      let all = [];
      let ok = true;
      let total = 0;
      try {
        for (let p = 1; p <= pages; p++) {
          const q = new URLSearchParams({
            url: d.shop,
            shop_domain: d.shop,
            platform: 'shopify',
            review_selection: 'all',
            page: String(p),
            per_page: String(PER_PAGE),
          });
          const r = await fetch(FEED + '?' + q.toString(), { credentials: 'omit' });
          if (!r.ok) {
            ok = false;
            break;
          }
          const j = await r.json();
          total = +(j && j.total_count) || total;
          const got = j && Array.isArray(j.reviews) ? j.reviews : [];
          if (!got.length) break;
          all = all.concat(got);
          if (+j.total_pages > 0) pages = Math.min(MAX_PAGES, +j.total_pages);
        }
      } catch (e) {
        /* silent: whatever loaded is used; the server cards stay */
        ok = false;
      }
      /* Complete only when nothing failed and the distinct ids reach Judge.me's own total (a page cut short by the
         safety bound, or the feed repeating a record across pages, never counts as complete) */
      const distinct = new Set(all.map((r) => str(r && r.uuid)).filter(Boolean)).size;
      if (all.length) this.add(all, ok && total > 0 && distinct >= total);
    }

    add(all, complete) {
      const sc = this.sc;
      /* With the complete live list in hand, a card none of whose ids Judge.me still returns is stale (hidden or
         deleted after the metafield snapshot was written). A card with no ids is never judged. */
      const live = complete ? new Set(all.map((r) => str(r && r.uuid)).filter(Boolean)) : null;
      const stale = (c) => {
        if (!live) return false;
        const ids = str(c.getAttribute('data-rv-ids')).split(/\s+/).filter(Boolean);
        return ids.length > 0 && !ids.some((id) => live.has(id));
      };
      /* Keep the card in view where it is when cards land or leave before it (a review newer than the metafield, a
         hidden one). Measured on screen, so a browser that already anchors the scroll itself (Chrome) isn't moved
         twice. The card kept is one that stays. */
      const left = sc.getBoundingClientRect().left;
      const keep = sc.scrollLeft > 1 ? Array.from(this.list.children).find((c) => !stale(c) && c.getBoundingClientRect().right > left + 1) : null;
      const keepX = keep ? keep.getBoundingClientRect().left : 0;
      let removed = 0;
      if (live) {
        Array.from(this.list.children).forEach((c) => {
          if (!stale(c)) return;
          if (c.contains(D.activeElement)) sc.focus({ preventScroll: true });
          c.remove();
          removed++;
        });
      }
      const have = new Set();
      this.list.querySelectorAll('[data-rv-ids]').forEach((li) => {
        li.getAttribute('data-rv-ids').split(/\s+/).forEach((id) => id && have.add(id));
      });
      const dedupe = this.dataset.dedupe === '1';
      const groups = new Map();
      all.forEach((r) => {
        if (!r || !r.uuid) return;
        const t = norm(r.title);
        const b = norm(r.body);
        const k = dedupe && (t || b) ? t + '|' + b + '|' + norm(r.reviewer_name) : 'id:' + r.uuid;
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k).push(r);
      });
      let added = 0;
      groups.forEach((g) => {
        const ids = g.map((r) => str(r.uuid));
        if (ids.some((id) => have.has(id))) return;
        try {
          this.insert(this.card(g[0], ids));
          ids.forEach((id) => have.add(id));
          added++;
        } catch (e) {
          /* one bad record never stops the rest */
        }
      });
      if (added || removed) {
        const dx = keep ? Math.round(keep.getBoundingClientRect().left - keepX) : 0;
        if (dx) {
          try {
            sc.scrollTo({ left: sc.scrollLeft + dx, behavior: 'instant' });
          } catch (e) {
            sc.scrollLeft += dx;
          }
        }
      }
      this.clamp();
      this.ends();
    }

    /* Row order: photo cards first (when data-order is photos_first), then newest first; ties keep their place */
    insert(li) {
      const pf = this.dataset.order !== 'newest';
      const rank = (c) => (pf && c.getAttribute('data-rv-photo') !== '1' ? 1 : 0);
      const date = (c) => c.getAttribute('data-rv-date') || '';
      const r = rank(li);
      const d = date(li);
      const before = Array.from(this.list.children).find((c) => rank(c) > r || (rank(c) === r && date(c) < d));
      this.list.insertBefore(li, before || null);
    }

    flavors() {
      if (this.fmap) return this.fmap;
      this.fmap = {};
      str(this.dataset.flavors)
        .split('||')
        .forEach((row) => {
          const [h, n, u] = row.split('|');
          if (h && n) this.fmap[h] = { n: dec(n), u: PRODUCT.test(str(u).split('?')[0]) ? str(u) : '' };
        });
      return this.fmap;
    }

    star(on) {
      const s = D.createElementNS(SVG_NS, 'svg');
      s.setAttribute('viewBox', '0 0 24 24');
      s.setAttribute('fill', on ? 'currentColor' : 'none');
      s.setAttribute('stroke', 'currentColor');
      s.setAttribute('stroke-width', '1.5');
      s.setAttribute('stroke-linejoin', 'round');
      s.setAttribute('aria-hidden', 'true');
      s.setAttribute('focusable', 'false');
      const p = D.createElementNS(SVG_NS, 'path');
      p.setAttribute('d', STAR);
      s.appendChild(p);
      return s;
    }

    picLink(base, cls) {
      const a = make('a', cls);
      a.href = picUrl(base, 1024);
      a.target = '_blank';
      a.rel = 'noopener nofollow';
      return a;
    }

    media(pics, name) {
      const d = this.dataset;
      const max = Math.max(1, parseInt(d.photosMax, 10) || 6);
      const total = pics.length;
      const alt = (n) => fill(d.photoAlt, { n, total, name });
      const box = make('div', 'rv-revcard__media');

      const lead = this.picLink(pics[0], 'rv-revcard__lead');
      const img = make('img');
      img.src = picUrl(pics[0], 480);
      img.srcset = [320, 480, 640, 800].map((w) => picUrl(pics[0], w) + ' ' + w + 'w').join(', ');
      img.sizes = SIZES;
      img.width = 480;
      img.height = 480;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.alt = alt(1);
      lead.appendChild(img);
      box.appendChild(lead);

      if (total > 1) {
        const ul = make('ul', 'rv-revcard__thumbs');
        ul.setAttribute('role', 'list');
        pics.slice(1, max).forEach((b, i) => {
          const li = make('li');
          const a = this.picLink(b, 'rv-revcard__thumb');
          const t = make('img');
          t.src = picUrl(b, 160);
          t.width = 56;
          t.height = 56;
          t.loading = 'lazy';
          t.decoding = 'async';
          t.alt = alt(i + 2);
          a.appendChild(t);
          li.appendChild(a);
          ul.appendChild(li);
        });
        if (total > max) {
          const li = make('li');
          const a = this.picLink(pics[max], 'rv-revcard__morepics');
          a.appendChild(D.createTextNode(fill(d.morePhotos, { count: total - max })));
          a.appendChild(make('span', 'rv-vh', ' ' + alt(max + 1)));
          li.appendChild(a);
          ul.appendChild(li);
        }
        box.appendChild(ul);
      }
      return box;
    }

    flavorLink(r) {
      const d = this.dataset;
      if (isImported(r) && d.flavorImported !== '1') return null;
      const path = str(r.product_url).split('?')[0];
      if (!PRODUCT.test(path)) return null;
      const f = this.flavors()[path.slice('/products/'.length)];
      const name = f
        ? f.n
        : dec(r.product_title)
            .split(' Electrolyte Powder')
            .join('')
            .split(' electrolyte powder')
            .join('')
            .split('Rhovive ')
            .join('')
            .trim();
      if (!name) return null;
      const a = make('a', 'rv-revcard__flavor', name);
      a.href = f && f.u ? f.u : path;
      return a;
    }

    card(r, ids) {
      const d = this.dataset;
      const n = 'j' + ++this.n;
      const titleId = 'rv-rt-' + d.sid + '-' + n;
      const bodyId = 'rv-rb-' + d.sid + '-' + n;
      const pics = (Array.isArray(r.pictures_urls) ? r.pictures_urls : []).map(picBase).filter(Boolean);
      const date = str(r.created_at).slice(0, 10);
      const rawName = dec(r.reviewer_name).trim();
      const name = r.is_anonymous_reviewer || !rawName ? str(d.anonymous) : rawName;
      const rating = Math.max(0, Math.min(5, Math.round(+r.rating || 0)));

      const li = make('li', 'rv-revcard');
      li.setAttribute('data-rv-ids', ids.join(' '));
      li.setAttribute('data-rv-date', date);
      li.setAttribute('data-rv-photo', pics.length ? '1' : '0');
      const art = make('article', 'rv-revcard__in');
      art.setAttribute('aria-labelledby', titleId);
      li.appendChild(art);

      if (pics.length) art.appendChild(this.media(pics, name));

      const stars = make('p', 'rv-revcard__stars');
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', fill(d.stars, { rating }));
      for (let i = 1; i <= 5; i++) stars.appendChild(this.star(i <= rating));
      art.appendChild(stars);

      const title = dec(r.title).trim();
      const h = make('h3', title ? 'rv-revcard__title' : 'rv-vh', title || fill(d.untitled, { name }));
      h.id = titleId;
      art.appendChild(h);

      const body = make('p', 'rv-revcard__body', dec(r.body).trim());
      body.id = bodyId;
      art.appendChild(body);

      const more = make('button', 'rv-textbtn rv-revcard__more', str(d.more));
      more.type = 'button';
      more.hidden = true;
      more.setAttribute('aria-expanded', 'false');
      more.setAttribute('aria-controls', bodyId);
      art.appendChild(more);

      const meta = make('p', 'rv-revcard__meta');
      const who = make('span', 'rv-revcard__who');
      who.appendChild(make('span', 'rv-revcard__name', name));
      if (d.showVerified === '1' && r.verified_buyer === true) {
        who.appendChild(D.createTextNode(' '));
        who.appendChild(make('span', 'rv-revcard__verified', str(d.verified)));
      }
      meta.appendChild(who);
      meta.appendChild(D.createTextNode(' '));
      const when = make('span', 'rv-revcard__when');
      const shown = date.length === 10 ? fmtDate(date) : '';
      if (shown) {
        const t = make('time', 'rv-revcard__date', shown);
        t.setAttribute('datetime', date);
        when.appendChild(t);
      }
      /* "Imported review" (setting imported_label, blank = off), as the Liquid */
      if (d.imported && isImported(r)) {
        if (when.firstChild) when.appendChild(D.createTextNode(' '));
        when.appendChild(make('span', 'rv-revcard__src', d.imported));
      }
      const fl = this.flavorLink(r);
      if (fl) {
        when.appendChild(D.createTextNode(' '));
        when.appendChild(fl);
      }
      meta.appendChild(when);
      art.appendChild(meta);
      return li;
    }
  }

  customElements.define('rv-review-rail', RvReviewRail);
})();
